/**
 * Detalle full-screen de una operación SNT (despacho o recepción).
 *
 * Drill-down regulatorio:
 *   - Header con tipo + estado + código Tx + fecha + acciones (PDF / cancelar)
 *   - Datos del remito (origen, destino, items con cantidades)
 *   - Mensajes ANMAT (timeline request/response del SNT)
 *   - Series asociadas con link a cada timeline individual
 *   - Log API filtrado por esta operación
 *   - Observaciones del audit (razones de confirmación, cancelación, edición)
 *
 * URL: /operaciones/:opId — shareable para QA.
 */

import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from '@/lib/notify';
import {
  ArrowLeft,
  Loader2,
  FileDown,
  Package,
  MapPin,
  User,
  Calendar,
  Hash,
  MessageSquare,
  AlertTriangle,
  GitBranch,
  Globe,
  Activity,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  useOperaciones,
  useUbicaciones,
  useRemitos,
  useClientes,
  useProductos,
  useSeries,
  useEventosAnmat,
  useCancelarTransaccionMutation,
  useCancelarRecepcionMutation,
} from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import { useAuditStore } from '@/stores/audit';
import { useApiLogStore } from '@/stores/apilog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { fmtDateTime, fmtDateTimeLong, fmtRemitoNumero, fmtNum } from '@/lib/format';
import { puedeActuar } from '@/domain/rbac';
import { generarPdfTrazabilidad } from '@/features/pdf/trazabilidad-pdf';
import {
  FirmaElectronicaModal,
  type FirmaElectronicaResult,
} from '@/features/auth/FirmaElectronicaModal';
import {
  ANMAT_LABEL,
  ANMAT_BADGE,
  TIPO_OPERACION_LABEL_LARGO as TIPO_LABEL,
} from '@/domain/anmat/labels';

export function OperacionDetallePage() {
  const { opId } = useParams<{ opId: string }>();
  const navigate = useNavigate();
  const usuario = useSessionStore((s) => s.usuario);
  const addAuditEntry = useAuditStore((s) => s.addEntry);
  const auditEntries = useAuditStore((s) => s.entries);
  const apiLogEntries = useApiLogStore((s) => s.entries);

  const { data: operaciones = [], isLoading } = useOperaciones();
  const { data: ubicaciones = [] } = useUbicaciones();
  const { data: remitos = [] } = useRemitos();
  const { data: clientes = [] } = useClientes();
  const { data: productos = [] } = useProductos();
  const { data: series = [] } = useSeries();
  const { data: eventosAnmat = [] } = useEventosAnmat();

  const cancelarTransaccionMutation = useCancelarTransaccionMutation();
  const cancelarRecepcionMutation = useCancelarRecepcionMutation();

  const [opACancelar, setOpACancelar] = useState(false);

  const op = useMemo(() => operaciones.find((o) => o.id === opId), [operaciones, opId]);

  // Origen y destino resueltos según tipo
  const { origen, destino, remito, cliente } = useMemo(() => {
    if (!op) return { origen: null, destino: null, remito: null, cliente: null };
    const origen = ubicaciones.find((u) => u.id === op.ubicacionId) ?? null;
    let destino = op.destinoUbicacionId
      ? ubicaciones.find((u) => u.id === op.destinoUbicacionId) ?? null
      : null;
    const remito = op.remitoNumero
      ? remitos.find((r) => r.numero === op.remitoNumero) ?? null
      : null;
    let cliente = null;
    if (remito?.clienteId) {
      cliente = clientes.find((c) => c.id === remito.clienteId) ?? null;
    }
    // Si es recepción, el "destino" es la propia op.ubicacionId, y el "origen"
    // viene de la op origen
    if (op.tipo === 'recepcion') {
      destino = origen; // donde se recepciona
      const opOrig = op.opOrigenId
        ? operaciones.find((x) => x.id === op.opOrigenId)
        : null;
      const origenReal = opOrig ? ubicaciones.find((u) => u.id === opOrig.ubicacionId) : null;
      return { origen: origenReal, destino, remito, cliente };
    }
    return { origen, destino, remito, cliente };
  }, [op, ubicaciones, remitos, clientes, operaciones]);

  // Evento ANMAT resuelto
  const evento = useMemo(() => {
    if (!op?.eventoAnmatId) return null;
    return eventosAnmat.find((e) => e.id === op.eventoAnmatId) ?? null;
  }, [op, eventosAnmat]);

  // Observaciones: entradas del audit relacionadas con esta operación.
  // Incluye las acciones de despacho.confirmar, recepcion.confirmar,
  // anmat.cancelar_transaccion, recepcion.cancelar, recepcion.anular_confirmada,
  // recepcion.divergencia, evento_anmat.override, anmat.reintentar,
  // anmat.editar, etc.
  const observaciones = useMemo(() => {
    if (!op) return [];
    return auditEntries
      .filter((e) => e.entidadTipo === 'operacion' && e.entidadId === op.id)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [auditEntries, op]);

  // API log filtrado por esta operación
  const apiCalls = useMemo(() => {
    if (!op) return [];
    return apiLogEntries
      .filter((e) => e.operacionId === op.id)
      .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  }, [apiLogEntries, op]);

  const handleVolver = () => {
    // History back para que el usuario vuelva donde estaba (Despacho, Recepción, ANMAT)
    void navigate(-1);
  };

  const handleDescargarPdf = () => {
    if (!op) return;
    void generarPdfTrazabilidad({
      operacion: op,
      series,
      productos,
      ubicaciones,
      usuario,
      eventoAnmat: evento,
    }).then(() => {
      addAuditEntry({
        categoria: 'lectura_sensible',
        accion: 'operacion.descargar_pdf',
        entidadTipo: 'operacion',
        entidadId: op.id,
        razon: `PDF de trazabilidad generado desde detalle de ${op.id}`,
      });
      toast.success(`PDF de ${op.id} generado`);
    }).catch((err: Error) => toast.error(`Error: ${err.message}`));
  };

  const handleClickCancelar = () => {
    if (!op) return;
    const accionRequerida =
      op.tipo === 'recepcion' ? 'cancelar_recepcion' : 'cancelar_transaccion';
    if (!puedeActuar(usuario, accionRequerida)) {
      addAuditEntry({
        categoria: 'sesion',
        accion: 'permiso.denegado',
        detalle: `Cancelar ${op.tipo} sin permiso desde detalle`,
      });
      toast.error(`Sin permiso para cancelar ${op.tipo === 'recepcion' ? 'recepción' : 'transacción'}`);
      return;
    }
    setOpACancelar(true);
  };

  const handleFirmaCancelar = (firma: FirmaElectronicaResult) => {
    setOpACancelar(false);
    if (!firma.firmado || !op || !usuario || !firma.razon) return;
    const esRecepcion = op.tipo === 'recepcion';
    const eraConfirmada = op.anmat.estado === 'confirmada';
    const mutation = esRecepcion ? cancelarRecepcionMutation : cancelarTransaccionMutation;
    mutation.mutate(
      { operacionId: op.id, usuarioId: usuario.id, razon: firma.razon },
      {
        onSuccess: (data) => {
          const accion = esRecepcion
            ? eraConfirmada
              ? 'recepcion.anular_confirmada'
              : 'recepcion.cancelar'
            : 'anmat.cancelar_transaccion';
          addAuditEntry({
            categoria: 'operacion_critica',
            accion,
            entidadTipo: 'operacion',
            entidadId: op.id,
            valoresAntes: { estado: op.anmat.estado, seriesIds: op.seriesIds, tipo: op.tipo },
            valoresDespues: {
              estado: 'cancelada',
              fechaCancelacion: data.operacion.fechaCancelacion,
              seriesRevertidas: op.seriesIds.length,
            },
            razon: firma.razon,
            significadoFirma: firma.significado,
          });
          toast.success(
            esRecepcion
              ? eraConfirmada
                ? `Recepción anulada · series revertidas y despacho origen vuelve a "informada"`
                : `Recepción cancelada · series revertidas`
              : `Transacción cancelada · series revertidas`,
          );
        },
        onError: (err) => toast.error(`Error: ${err.message}`),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando operación...
      </div>
    );
  }
  if (!op) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Operación <code className="font-mono">{opId}</code> no encontrada.{' '}
          <button onClick={handleVolver} className="underline">
            Volver
          </button>
        </AlertDescription>
      </Alert>
    );
  }

  const esCancelada = op.anmat.estado === 'cancelada';
  const esConfirmada = op.anmat.estado === 'confirmada';
  const esCancelable = !esCancelada && (op.tipo === 'recepcion' || !esConfirmada);
  const puedePdf = op.anmat.estado === 'informada' || op.anmat.estado === 'confirmada';

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardContent className="pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleVolver}
            className="-ml-2 mb-3 text-xs"
          >
            <ArrowLeft className="mr-1 h-3 w-3" /> Volver
          </Button>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xl font-bold text-everex-azul">{op.id}</span>
                <span className={cn('badge', ANMAT_BADGE[op.anmat.estado])}>
                  {ANMAT_LABEL[op.anmat.estado]}
                </span>
                {op.tipo === 'recepcion' && origen?.externa && (
                  <span className="badge badge-orange">
                    <Globe className="mr-1 inline h-3 w-3" />
                    Origen externo
                  </span>
                )}
              </div>
              <div className="mt-1 text-sm text-gray-600">{TIPO_LABEL[op.tipo]}</div>
              {op.remitoNumero && (
                <div className="mt-1 font-mono text-xs text-gray-600">
                  Remito {fmtRemitoNumero(op.remitoNumero)}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {puedePdf && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDescargarPdf}
                  className="text-xs"
                >
                  <FileDown className="mr-1 h-3 w-3" /> Descargar PDF
                </Button>
              )}
              {esCancelable && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClickCancelar}
                  className={cn(
                    'text-xs text-red-600 hover:bg-red-50',
                    op.tipo === 'recepcion' && esConfirmada && 'border-amber-300',
                  )}
                >
                  {op.tipo === 'recepcion' && esConfirmada ? (
                    <>
                      <AlertTriangle className="mr-1 h-3 w-3" /> Anular
                    </>
                  ) : (
                    'Cancelar'
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Columna izquierda: datos + series */}
        <div className="space-y-4 lg:col-span-2">
          {/* Datos del remito */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4" />
                Datos del remito
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
              <DataField label="Origen" icon={<MapPin className="h-3 w-3" />}>
                <b>{origen?.nombre ?? '—'}</b>
                <div className="font-mono text-[11px] text-gray-600">
                  GLN {origen?.gln ?? '—'} · {origen?.tipoActor ?? ''}
                </div>
              </DataField>
              <DataField label="Destino" icon={<MapPin className="h-3 w-3" />}>
                {cliente ? (
                  <>
                    <b>{cliente.descripcion}</b>
                    <div className="text-[11px] text-gray-600">CUIT {cliente.cuit}</div>
                  </>
                ) : destino ? (
                  <>
                    <b>{destino.nombre}</b>
                    <div className="font-mono text-[11px] text-gray-600">
                      GLN {destino.gln} · {destino.tipoActor}
                    </div>
                  </>
                ) : (
                  <span className="text-gray-600">— (terminal)</span>
                )}
              </DataField>
              <DataField label="Fecha" icon={<Calendar className="h-3 w-3" />}>
                {fmtDateTimeLong(op.fecha)}
              </DataField>
              <DataField label="Usuario" icon={<User className="h-3 w-3" />}>
                <span className="font-mono">{op.usuarioId}</span>
              </DataField>
              <DataField label="Código Tx SNT" icon={<Hash className="h-3 w-3" />}>
                <span className="font-mono">{op.anmat.codigoTransaccion ?? '—'}</span>
              </DataField>
              <DataField label="Intentos" icon={<Activity className="h-3 w-3" />}>
                {op.anmat.intentos}
              </DataField>
              {evento && (
                <div className="md:col-span-2">
                  <div className="text-[11px] uppercase text-gray-600">Evento ANMAT</div>
                  <div className="mt-1 text-sm">
                    <span className="font-mono">#{evento.id}</span> · {evento.evento}
                  </div>
                  <div className="font-mono text-[11px] text-gray-600">
                    {evento.informador} → {evento.origen} → {evento.destino ?? '— (terminal)'}
                  </div>
                </div>
              )}
              {op.fechaCancelacion && (
                <div className="md:col-span-2 rounded border border-red-200 bg-red-50 px-3 py-2">
                  <div className="text-[11px] uppercase text-red-700">Cancelada</div>
                  <div className="text-xs text-red-900">
                    {fmtDateTimeLong(op.fechaCancelacion)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Items del remito (si existe) */}
          {remito && remito.items.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Items del remito</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>GTIN</TableHead>
                      <TableHead>Producto</TableHead>
                      <TableHead>Lote</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {remito.items.map((it, i) => {
                      const prod = productos.find((p) => p.gtin === it.gtin);
                      return (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{it.gtin}</TableCell>
                          <TableCell className="text-xs">{prod?.nombre ?? '—'}</TableCell>
                          <TableCell className="font-mono text-xs">{it.lote}</TableCell>
                          <TableCell className="text-right text-xs">
                            <b>{fmtNum(it.cantidad)}</b>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Series asociadas */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4" />
                Series asociadas
                <span className="text-[11px] font-normal text-gray-600">
                  ({fmtNum(op.seriesIds.length)})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid max-h-72 grid-cols-1 gap-1 overflow-y-auto text-[11px] sm:grid-cols-2 md:grid-cols-3">
                {op.seriesIds.slice(0, 200).map((sid) => (
                  <Link
                    key={sid}
                    to={`/trazabilidad?serie=${encodeURIComponent(sid)}`}
                    className="flex items-center gap-1 truncate rounded bg-gray-50 px-2 py-1 font-mono text-everex-azul hover:bg-everex-azul-bg"
                    title="Ver timeline de esta serie"
                  >
                    <GitBranch className="h-3 w-3 shrink-0" />
                    {sid}
                  </Link>
                ))}
              </div>
              {op.seriesIds.length > 200 && (
                <div className="mt-2 text-[11px] text-gray-600">
                  Mostrando 200 de {fmtNum(op.seriesIds.length)}. Para ver todas, descargá el PDF.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Columna derecha: mensajes ANMAT + log API + observaciones */}
        <div className="space-y-4">
          {/* Mensajes ANMAT */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <MessageSquare className="h-4 w-4" />
                Mensajes ANMAT
                <span className="text-[11px] font-normal text-gray-600">
                  ({op.anmat.mensajes.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {op.anmat.mensajes.length === 0 ? (
                <div className="py-4 text-center text-xs text-gray-600">Sin mensajes</div>
              ) : (
                op.anmat.mensajes.map((m, i) => (
                  <div
                    key={i}
                    className={cn(
                      'rounded border-l-2 p-2 text-xs',
                      m.tipo === 'request'
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-green-400 bg-green-50',
                    )}
                  >
                    <div className="flex items-center justify-between text-[11px] uppercase text-gray-600">
                      <span>{m.tipo}</span>
                      <span>{fmtDateTime(m.ts)}</span>
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-gray-800">{m.body}</div>
                  </div>
                ))
              )}
              {op.anmat.ultimoError && (
                <Alert variant="destructive" className="mt-2">
                  <XCircle className="h-3 w-3" />
                  <AlertDescription className="text-[11px]">
                    <b>{op.anmat.ultimoError.code}</b> · {op.anmat.ultimoError.msg}{' '}
                    <span className="text-gray-600">({op.anmat.ultimoError.severidad})</span>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Log API */}
          {apiCalls.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Activity className="h-4 w-4" />
                  Log API
                  <span className="text-[11px] font-normal text-gray-600">
                    ({apiCalls.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {apiCalls.map((e) => (
                  <div
                    key={e.id}
                    className="rounded border border-gray-100 p-2 text-[11px]"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        {e.ok ? (
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                        ) : (
                          <XCircle className="h-3 w-3 text-red-600" />
                        )}
                        <span className="font-semibold uppercase text-gray-600">
                          {e.sistema}
                        </span>
                        <span className="font-mono">{e.metodo}</span>
                      </div>
                      <span className="text-gray-600">{fmtDateTime(e.ts)}</span>
                    </div>
                    <div className="mt-0.5 font-mono text-gray-600">{e.endpoint}</div>
                    <div className="text-gray-600">
                      Status: {e.status} {e.duracionMs ? `· ${e.duracionMs}ms` : ''}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Observaciones del audit */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <MessageSquare className="h-4 w-4" />
                Observaciones
                <span className="text-[11px] font-normal text-gray-600">
                  ({observaciones.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {observaciones.length === 0 ? (
                <div className="py-4 text-center text-xs text-gray-600">
                  Sin observaciones registradas
                </div>
              ) : (
                <div className="space-y-2">
                  {observaciones.map((e) => (
                    <div
                      key={e.id}
                      className="rounded border border-gray-100 bg-gray-50 p-2 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="badge badge-blue text-[11px]">{e.accion}</span>
                        <span className="text-[11px] text-gray-600">
                          {fmtDateTime(e.timestamp)}
                        </span>
                      </div>
                      <div className="mt-1 font-semibold text-gray-700">
                        {e.usuarioNombreSnap}
                      </div>
                      {e.razon && (
                        <div className="mt-1 italic text-gray-700">"{e.razon}"</div>
                      )}
                      {e.significadoFirma && (
                        <div className="mt-1 text-[11px] text-gray-600">
                          Firma: {e.significadoFirma}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <FirmaElectronicaModal
        open={opACancelar}
        titulo={
          op.tipo === 'recepcion' && esConfirmada
            ? 'Anular recepción confirmada (equivale a devolución)'
            : `Cancelar ${op.tipo === 'recepcion' ? 'recepción' : 'transacción'} ANMAT`
        }
        resumen={[
          `Operación ${op.id}`,
          TIPO_LABEL[op.tipo],
          `${op.seriesIds.length} series`,
          `Estado actual: ${ANMAT_LABEL[op.anmat.estado]}`,
          ...(op.tipo === 'recepcion' && esConfirmada
            ? ['ATENCIÓN: anular una recepción confirmada revierte el despacho origen a "informada".']
            : []),
        ].join(' · ')}
        requiereRazon
        entidad={{ tipo: 'operacion', id: op.id }}
        onResolve={handleFirmaCancelar}
      />
    </div>
  );
}

function DataField({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[11px] uppercase text-gray-600">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  );
}
